<script setup>
import { ref, reactive, computed, onMounted, watch, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { Head, useHead } from '@vueuse/head';

// Component Imports
import LoadingManager from '@/components/LoadingManager.vue';
import Footer from '../../components/Footer';
import StickyHeaderCruises from '../../components/StickyHeaderCruise.vue';
import VideoPlayer from '../../components/VideoPlayer';
import CruiseAlternative from '@/components/CruiseAlternative.vue';
import VueEasyLightbox from 'vue-easy-lightbox';
import ShipDetail from '@/components/ShipDetail.vue';

// Composables, Utilities, and Modules
import { Api } from '../../modules/Api';
import { Dictionary } from '../../modules/Dictionary';
import { Price } from '@/utilities/price';
import { stringifyQuery } from '@/utilities/url';
import { formatDateLocalFixed, parseIsoDate } from '@/utilities/dateFormatter';
import formatPriceUtil from '../../utilities/formatPrice';
import { CURRENT_CURRENCY } from '@/utilities/currency';
import { LANGUAGE, VUE_APP_BASE_URL } from '@/env';

// Static Assets
import cruisersVideo from '@/assets/videos/cruisers.webm';
import cruiserImg from '@/assets/imgs/posters/cruiser-tour.jpg';
import dayAtSeaImg from '@/assets/imgs/day-at-sea.jpg';
import otherOffersStyles from '@/assets/sass/components/other-offers.module.scss';
import SkeletonBox from '@/components/SkeletonBox.vue';

const CruiseBanner = defineAsyncComponent({
    loader: () => import(/** webpackChunkName: "CruiseBanner" */'@/components/CruiseBanner.vue'),
});

// --- PROPS ---
const props = defineProps({
    cruise_code: String,
});

// --- COMPOSABLES ---
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

// --- CONSTANTS ---
const api = new Api(t);
const dict = new Dictionary();

// --- STATE ---
const details = ref({});
const recommendedCruises = ref([]);
const shipInfo = ref({});
const msc = ref(null);
const itineraryGallery = reactive({
    imgs: [],
    visible: false,
    index: 0,
});

const schemaItinerary = computed(() => {
    if (!details.value.itinerary_detail_steps) return [];
    return details.value.itinerary_detail_steps.map((step, index) => {
        const placeName = step.day_at_sea
            ? t('LABELS.day-at-sea')
            : step.departure_port_name?.[LANGUAGE] || step.arrival_port_name?.[LANGUAGE];

        return {
            '@type': 'ListItem',
            'position': index + 1,
            'item': {
                '@type': 'Place',
                'name': placeName
            }
        };
    });
});

const canonicalUrl = computed(() => {
    return VUE_APP_BASE_URL + route.path;
});


// --- TEMPLATE REFS ---
const loadingManager = ref(null);

// --- METHODS ---
const daysInflected = (days_num) => {
    return dict.getWord('days', days_num, LANGUAGE);
};

const formatDate = (date) => {
    if (!date) return null;
    return formatDateLocalFixed(parseIsoDate(date));
};

const formatPrice = (price) => {
    return formatPriceUtil(price);
};

const getNotFoundUrl = () => {
    return {
        path: `/rezervace/${props.cruise_code}/nenalezeno/`,
        query: route.query,
    };
};

const showItineraryGallery = (index) => {
    itineraryGallery.index = index;
    itineraryGallery.visible = true;
};

const handleItineraryClick = (event) => {
    if (event.target.tagName !== 'IMG') {
        return;
    }
    const imgSrc = event.target.src;
    const index = itineraryGallery.imgs.indexOf(imgSrc);

    if (index !== -1) {
        showItineraryGallery(index);
    }
};

const processItinerarySteps = (departureDate, fullItinerary) => {
    const itinerary = {};
    for (const step in fullItinerary) {
        const key = parseInt(step, 10);
        const nextKey = key + 1;
        const itineraryData = fullItinerary[key];
        if (typeof itinerary[key] === 'undefined') {
            itinerary[key] = {};
        }

        const stepDate = new Date(departureDate);
        stepDate.setDate(stepDate.getDate() + (itineraryData.departure_day - 1));

        itinerary[key].departure_day = itineraryData.departure_day;
        itinerary[key].departure_time = itineraryData.departure_time || undefined;
        itinerary[key].departure_port_name = itineraryData.departure_port_name;
        itinerary[key].departure_port_description = itineraryData.departure_port_description;
        itinerary[key].date = `${stepDate.getDate()}. ${stepDate.getMonth() + 1}. ${stepDate.getFullYear()}`;
        itinerary[key].original_date = departureDate;

        if (nextKey <= fullItinerary.length) {
            if (typeof itinerary[nextKey] === 'undefined') {
                itinerary[nextKey] = {};
            }
            itinerary[nextKey].arrival_day = itineraryData.arrival_day;
            itinerary[nextKey].arrival_time = itineraryData.arrival_time || undefined;

            if (nextKey === fullItinerary.length) {
                const arrivalDate = new Date(departureDate);
                arrivalDate.setDate(arrivalDate.getDate() + (itineraryData.arrival_day - 1));
                itinerary[nextKey].arrival_port_name = itineraryData.arrival_port_name;
                itinerary[nextKey].arrival_port_description = itineraryData.arrival_port_description;
                itinerary[nextKey].date = `${arrivalDate.getDate()}. ${arrivalDate.getMonth() + 1}. ${arrivalDate.getFullYear()}`;
                itinerary[nextKey].original_date = departureDate;
            }
        }
    }

    const itineraryFull = [];
    for (const x in itinerary) {
        const currentStep = itinerary[x];
        if (x > 0) {
            const previousStep = itinerary[x - 1];
            if (
                typeof previousStep.departure_day !== 'undefined' &&
                typeof currentStep.arrival_day !== 'undefined' &&
                currentStep.arrival_day - previousStep.departure_day > 1
            ) {
                const firstDay = previousStep.departure_day + 1;
                const lastDay = currentStep.arrival_day - 1;
                const firstDate = new Date(previousStep.original_date);
                firstDate.setDate(firstDate.getDate() + (firstDay - 1));
                const lastDate = new Date(currentStep.original_date);
                lastDate.setDate(lastDate.getDate() + (lastDay - 1));
                itineraryFull.push({
                    day_at_sea: true,
                    first_day: firstDay,
                    last_day: lastDay,
                    date: firstDay === lastDay
                        ? `(${firstDate.getDate()}. ${firstDate.getMonth() + 1}. ${firstDate.getFullYear()})`
                        : `(${firstDate.getDate()}. ${firstDate.getMonth() + 1}. ${firstDate.getFullYear()} - ${lastDate.getDate()}. ${lastDate.getMonth() + 1}. ${lastDate.getFullYear()})`,
                });
            }
        }
        itineraryFull.push(currentStep);
    }
    return itineraryFull;
};

const getshipInfo = async () => {
    const ships = await api.getShips();
    const currentShip = await api.addShipGallery(
        ships.find((ship) => ship.name[LANGUAGE] === details.value.ship_name[LANGUAGE])
    );
    console.debug(currentShip);
    return currentShip || {};
};


// --- COMPUTED ---
const priceText = computed(() => {
    if (!details.value?.price) return '';
    return `${formatPrice(details.value.price[CURRENT_CURRENCY].amount_decimal)} ${details.value.price[CURRENT_CURRENCY].currency}`;
});

const fullPriceText = computed(() => {
    if (!details.value?.price) return '';
    return `${formatPrice(details.value.price[CURRENT_CURRENCY].amount_decimal)} ${details.value.price[CURRENT_CURRENCY].currency}`;
});

const secondPriceText = computed(() => {
    if (!details.value?.price) return '';
    return `${formatPrice(details.value.price[CURRENT_CURRENCY].amount_decimal)} ${details.value.price[CURRENT_CURRENCY].currency}`;
});

const urlCrewSearch = computed(() => {
    return `?${stringifyQuery(route.query)}`;
});

const totalCrew = computed(() => {
    return details.value.people;
});

// --- WATCHERS ---
watch(details, (newDetails) => {
    if (!newDetails || !newDetails.map_url) return;

    const imgRegex = /<img[^>]+src="([^">]+)"/g;

    const stepImages = newDetails.itinerary_detail_steps?.flatMap(step => {
        if (step.day_at_sea) {
            return [dayAtSeaImg];
        }

        const description = step.departure_port_description?.[LANGUAGE] || step.arrival_port_description?.[LANGUAGE] || '';

        return [...description.matchAll(imgRegex)].map(match => match[1]);
    });

    itineraryGallery.imgs = [newDetails.map_url, ...stepImages];
});


// --- LIFECYCLE ---
onMounted(async () => {
    loadingManager.value.start(3);

    const cruiseDetailsPromise = loadingManager.value.addTask(
        t('STATUSES.loading-trip-info'),
        2,
        api.getCruiseDetail(
            props.cruise_code,
        ).then((cruiseData) => {
            if (!cruiseData) {
                router.push(getNotFoundUrl());
            } else {
                cruiseData.itinerary_detail_steps = processItinerarySteps(
                    cruiseData.departure_date,
                    cruiseData.full_itinerary
                );
                details.value = cruiseData;
                msc.value = cruiseData.import_source === 'msc';
            }
            return cruiseData;
        })
    );

    const shipInfoPromise = loadingManager.value.addTask(
        t('STATUSES.loading-ship-info'),
        3,
        cruiseDetailsPromise.then((cruiseData) => {
            if (cruiseData) {
                return getshipInfo().then((info) => {
                    shipInfo.value = info;
                    return info;
                });
            }
            return null;
        })
    );

    const recommendedCruisesPromise = loadingManager.value.addTask(
        t('STATUSES.loading-recommended-cruises'),
        1,
        cruiseDetailsPromise.then((cruiseData) => {
            if (cruiseData) {
                return api.getShipCruises(
                    cruiseData.ship_code,
                    cruiseData.itinerary_code
                ).then((cruises) => {
                    recommendedCruises.value = cruises
                        .filter(c => c.cruise_code !== cruiseData.cruise_code)
                        .map(c => ({
                            ...c,
                            price: new Price(c.price),
                            departure_date: parseIsoDate(c.departure_date),
                        }));
                });
            }
            return null;
        })
    );

    await Promise.all([
        cruiseDetailsPromise,
        shipInfoPromise,
        recommendedCruisesPromise,
    ]);

    loadingManager.value.stop();
    console.debug('Loaded details:', details.value);

    if (!details.value) {
        return;
    }

    const priceExpiration = new Date();
    priceExpiration.setHours(priceExpiration.getHours() + 1);


    // Set Product snippet
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: `${t('LABELS.departure-from')} ${details.value?.departure_port_name?.[LANGUAGE]}`,
        description: t('META-DESCRIPTIONS.cruise-detail', {
            cruiseName: details.value?.departure_port_name?.[LANGUAGE],
        }),
        image: details.value?.map_url,
        sku: props.cruise_code,
        brand: {
            '@type': 'Brand',
            name: msc.value ? 'MSC Cruises' : 'Costa Cruises'
        },
        provider: {
            '@type': 'Organization',
            name: msc.value ? 'MSC Cruises' : 'Costa Cruises'
        },
        offers: [
            {
                '@type': 'Offer',
                priceCurrency: CURRENT_CURRENCY,
                price: details.value?.price?.[CURRENT_CURRENCY].amount_decimal,
                availability: 'https://schema.org/InStock',
                url: canonicalUrl.value,
                priceValidUntil: priceExpiration.toISOString(),
            }
        ],
        additionalProperty: [
            {
                '@type': 'PropertyValue',
                name: 'Trip Type',
                value: 'BoatTrip'
            },
            {
                '@type': 'PropertyValue',
                name: 'Duration',
                value: `P${details.value?.days}D` // e.g., P8D for 8 days
            },
            {
                '@type': 'PropertyValue',
                name: 'Departure Terminal',
                value: details.value?.departure_port_name?.[LANGUAGE]
            },
            {
                '@type': 'PropertyValue',
                name: 'Arrival Terminal',
                value: details.value?.arrival_port_name?.[LANGUAGE]
            }
        ],
        itinerary: {
            '@type': 'ItemList',
            numberOfItems: schemaItinerary.value.length,
            itemListElement: schemaItinerary.value
        },
    };

    useHead({
        script: {
            type: 'application/ld+json',
            children: () => JSON.stringify(schema),
        },
    });

});
</script>
<template>
    <div>

        <Head>
            <title>
                {{
                    t('TITLES.cruise-detail', {
                        cruiseName: details.departure_port_name?.[LANGUAGE],
                    })
                }}
            </title>
            <meta name="description" :content="t('META-DESCRIPTIONS.cruise-detail', {
                cruiseName: details.departure_port_name?.[LANGUAGE],
            })
                " />
        </Head>

        <header class="header-detail" ref="header-detail">
            <div class="breadcrumbs breadcrumbs--cruiser">
                <ul class="breadcrumbs__inner">
                    <li class="breadcrumbs__item">
                        <router-link :to="{
                            path: '/',
                            query: $route.query,
                        }" class="breadcrumbs__link">
                            nautyy .{{
                                LANGUAGE === 'cs'
                                    ? 'cz'
                                    : LANGUAGE === 'sk'
                                        ? 'sk'
                                        : ''
                            }}
                        </router-link>
                    </li>
                    <li class="breadcrumbs__item">
                        <router-link :to="{
                            path: '/vyhledat',
                            query: $route.query,
                        }" class="breadcrumbs__link">{{ details.destination_name?.[LANGUAGE] }}
                        </router-link>
                    </li>
                </ul>
            </div>
        </header>
        <main class="main main--cruiser cruiser-detail-page" :style="{
            'background-image': `url(${details.image})`,
        }">
            <StickyHeaderCruises :details="details" :url-crew-search="urlCrewSearch">
            </StickyHeaderCruises>
            <main class="cruiser-detail-page__main cruiser-detail">
                <router-link :to="{
                    path: `/vyhledat`,
                    query: $route.query,
                }" class="cruiser-detail-page__back-container cruiser-detail-page__back-container--cruiser">
                    <svg class="cruiser-detail-page__back-icon cruiser-detail-page__back-icon--cruiser">
                        <use href="@/assets/imgs/icons/sprite.svg#back-arrow"></use>
                    </svg>
                    {{ t('LABELS.back-to-search') }}
                </router-link>
                <div class="cruiser-detail__inner cruiser-detail-page__main-inner">
                    <div class="cruiser-detail__column">
                        <header class="cruiser-detail__header" ref="sticky-header-trigger">
                            <div class="cruiser-detail__header-part cruiser-detail__header-part--wide">
                                <h1 class="cruiser-detail__heading-1">
                                    <div class="cruiser-detail__subheading-1">
                                        {{ details.destination_name?.[LANGUAGE] || 'Načítám...' }}
                                    </div>
                                    {{ t('LABELS.departure-from') }}
                                    {{ details.departure_port_name?.[LANGUAGE] }}
                                </h1>
                                <div class="cruiser-detail__cruiser-name-container">
                                    <template v-if="details.ship_name">
                                        <template v-if="msc === true">
                                            <img class="cruiser-detail__cruiser-name-icon"
                                                src="@/assets/imgs/icons/msc.png" />
                                            <p class="cruiser-detail__cruiser-type">
                                                {{ t('LABELS.msc-cruises') }}
                                            </p>
                                        </template>
                                        <template v-else-if="msc === false">
                                            <svg class="cruiser-detail__cruiser-name-icon">
                                                <use href="@/assets/imgs/icons/sprite.svg#cruiser"></use>
                                            </svg>
                                            <p class="cruiser-detail__cruiser-type">
                                                {{ t('LABELS.costa-cruises') }}
                                            </p>
                                        </template>
                                    </template>
                                    <template v-else>
                                        <img class="cruiser-detail__cruiser-name-icon" />
                                        <p class="cruiser-detail__cruiser-type">
                                            {{ 'Načítám...' }}
                                        </p>
                                    </template>
                                    <p class="cruiser-detail__cruiser-name">
                                        {{ details.ship_name?.[LANGUAGE] || 'Načítám...' }}
                                    </p>
                                </div>
                            </div>
                            <div class="cruiser-detail__header-part">
                                <h3 class="cruiser-detail__header-heading">
                                    {{ t('LABELS.sail-date') }}
                                </h3>
                                <p class="cruiser-detail__header-text--wrap">
                                    <span class="cruiser-detail__header-text">
                                        <span class="cruiser-detail-page__arrow">→ </span>
                                        {{ formatDate(details.departure_date) }}
                                    </span>
                                    {{ ' ' }}
                                    <span class="cruiser-detail__header-text">
                                        {{ details.departure_port_name?.[LANGUAGE] }}
                                    </span>
                                </p>
                                <p class="cruiser-detail__header-text--wrap">
                                    <span class="cruiser-detail__header-text">
                                        <span class="cruiser-detail-page__arrow">← </span>
                                        {{ formatDate(details.arrival_date) }}
                                    </span>
                                    {{ ' ' }}
                                    <span class="cruiser-detail__header-text">
                                        {{ details.arrival_port_name?.[LANGUAGE] }}
                                    </span>
                                </p>
                            </div>
                            <div class="cruiser-detail__header-part">
                                <h3 class="cruiser-detail__header-heading">
                                    {{ t('LABELS.total-time') }}
                                </h3>
                                <p class="cruiser-detail__header-text">
                                    {{ details.days }}
                                    {{ daysInflected(details.days) }}
                                </p>
                            </div>
                            <div class="cruiser-detail__header-part cruiser-detail__header-part--price">
                                <router-link :to="{
                                    path: `/rezervace/${details.cruise_code}/kajuta-a-ubytovaci-balicek/`,
                                    query: $route.query,
                                }" class="cruiser-detail__header-btn btn btn--orange">{{ t('LABELS.choose-cabin') }}
                                </router-link>
                                <div class="cruiser-detail__price-container">
                                    <p class="cruiser-detail__price-box">
                                        <span class="cruiser-detail__price-label">
                                            <template v-if="totalCrew === 1">
                                                {{ t('LABELS.price-for-single-from') }}
                                            </template>
                                            <template v-else>
                                                {{ t('TEXTS.price-for-single-with') }}
                                                <br />
                                                {{
                                                    t('TEXTS.occupied-x-seats', {
                                                        x: totalCrew,
                                                    })
                                                }}
                                            </template>
                                        </span>
                                        <span class="cruiser-detail__price" :class="{
                                            'cruiser-detail__price--discounted': false,
                                        }">{{ priceText }}</span>
                                        <!-- TODO: DISCOUNT -->
                                        <sup class="cruiser-detail__price-full" v-if="false">{{ fullPriceText }}</sup>
                                    </p>
                                    <p class="cruiser-detail__second-price-text" v-if="false">
                                        {{
                                            t('TEXTS.price-for-x-from', {
                                                x: totalCrew,
                                            })
                                        }}
                                        {{ secondPriceText }}
                                    </p>
                                </div>
                            </div>
                        </header>
                        <main class="cruiser-detail__main">
                            <SkeletonBox v-if="!details.itinerary_detail_steps" height="800px" />
                            <section v-else class="cruiser-detail__section cruiser-detail__itinerary-container"
                                @click="handleItineraryClick">
                                <img :src="details?.map_url" :alt="t('LABELS.itinerary')"
                                    class="cruiser-detail__itinerary-img" />
                                <h2 class="cruiser-detail__heading-2">
                                    {{ t('LABELS.itinerary') }}
                                </h2>
                                <div class="cruiser-detail__itinerary">
                                    <div class="cruiser-detail__itinerary-item" v-for="(day, i) in details
                                        .itinerary_detail_steps" :key="i">
                                        <div v-if="day.day_at_sea">
                                            <h4 class="cruiser-detail__itinerary-day">
                                                {{ `${day.first_day}. ` }}
                                                {{
                                                    day.last_day !== undefined &&
                                                        day.first_day !== undefined &&
                                                        day.last_day !== day.first_day
                                                        ? ` - ${day.last_day}. `
                                                        : ''
                                                }}
                                                {{ t('LABELS.day').toUpperCase() }}
                                                {{ day.date }}
                                            </h4>
                                            <h3 class="cruiser-detail__itinerary-place">
                                                {{ t('LABELS.day-at-sea') }}
                                            </h3>
                                            <p class="cruiser-detail__day-at-sea-p">
                                                <img :src="dayAtSeaImg" class="cruiser-detail__dat-at-sea-image" />
                                            </p>
                                        </div>
                                        <div v-else>
                                            <h4 class="cruiser-detail__itinerary-day">
                                                {{
                                                    day.arrival_day === undefined
                                                        ? `${day.departure_day}. `
                                                        : `${day.arrival_day}. `
                                                }}
                                                {{
                                                    day.departure_day !==
                                                        undefined &&
                                                        day.arrival_day !== undefined &&
                                                        day.departure_day !==
                                                        day.arrival_day
                                                        ? ` - ${day.departure_day}.`
                                                        : ''
                                                }}
                                                {{ t('LABELS.day').toUpperCase() }}
                                                {{ ` (${day.date})` }}
                                            </h4>
                                            <h3 class="cruiser-detail__itinerary-place">
                                                {{
                                                    day.departure_port_name ===
                                                        undefined
                                                        ? day.arrival_port_name?.[
                                                        LANGUAGE
                                                        ]
                                                        : day.departure_port_name?.[
                                                        LANGUAGE
                                                        ]
                                                }}
                                            </h3>
                                            <h5 class="cruiser-detail__itinerary-time">
                                                {{
                                                    day.arrival_time !== undefined
                                                        ? `${t(
                                                            'LABELS.itinerary_arrival'
                                                        )}: ${day.arrival_time.slice(
                                                            0,
                                                            -3
                                                        )}`
                                                        : ''
                                                }}
                                                {{
                                                    day.arrival_time !==
                                                        undefined &&
                                                        day.departure_time !== undefined
                                                        ? ` / `
                                                        : ''
                                                }}
                                                {{
                                                    day.departure_time !== undefined
                                                        ? `${t(
                                                            'LABELS.itinerary_departure'
                                                        )}: ${day.departure_time.slice(
                                                            0,
                                                            -3
                                                        )}`
                                                        : ''
                                                }}
                                            </h5>
                                            <p class="cruiser-detail__itinerary-text" v-html="day.departure_port_description ===
                                                undefined
                                                ? day
                                                    .arrival_port_description?.[
                                                LANGUAGE
                                                ]
                                                : day
                                                    .departure_port_description?.[
                                                LANGUAGE
                                                ]
                                                "></p>
                                        </div>
                                    </div>
                                </div>
                                <router-link :to="{
                                    path: `/rezervace/${details.cruise_code}/kajuta-a-ubytovaci-balicek/`,
                                    query: $route.query,
                                }" class="cruiser-detail__btn btn btn--orange btn--standalone">{{
                                    t('LABELS.choose-cabin') }}
                                </router-link>
                            </section>

                            <section v-if="details.import_source !== 'msc'" class="cruiser-detail__section">
                                <Suspense>
                                    <CruiseBanner />
                                </Suspense>
                                <h2 class="cruiser-detail__heading-2">
                                    {{ t('LABELS.life-on-cruiser') }}
                                </h2>
                                <VideoPlayer :video-url="cruisersVideo" :poster-url="cruiserImg"
                                    class="cruiser-detail__video">
                                </VideoPlayer>
                                <router-link :to="{
                                    path: `/rezervace/${details.cruise_code}/kajuta-a-ubytovaci-balicek/`,
                                    query: $route.query,
                                }" class="cruiser-detail__btn btn btn--orange btn--standalone">{{
                                    t('LABELS.choose-cabin') }}
                                </router-link>
                            </section>
                        </main>
                    </div>
                    <div class="cruiser-detail__second-column">
                        <ShipDetail :ship-name="details.ship_name?.[LANGUAGE]" :ship-code="details.ship_code"
                            :images="shipInfo?.gallery?.map(i => i.url)">
                        </ShipDetail>
                        <div :class="otherOffersStyles['other-offers']">
                            <h2 :class="otherOffersStyles['other-offers__heading']">
                                {{ t('LABELS.cruise-dates') }}
                            </h2>
                            <CruiseAlternative v-for="cruise in recommendedCruises" :key="cruise.cruise_id"
                                :cruise-code="cruise.cruise_code" :departure-date="cruise.departure_date"
                                :price="cruise.price">
                            </CruiseAlternative>
                        </div>
                    </div>
                </div>
            </main>

        </main>
        <Footer></Footer>
        <LoadingManager ref="loadingManager"></LoadingManager>
        <VueEasyLightbox :visible="itineraryGallery.visible" :imgs="itineraryGallery.imgs"
            :index="itineraryGallery.index" @hide="itineraryGallery.visible = false" :zoom-disabled="true"
            :move-disabled="true"></VueEasyLightbox>
    </div>
</template>
<style lang="scss" scoped>
@import '@/assets/sass/components/cruiser-detail';
</style>
